<template>
  <div class="disbursement-history">	
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <span>Loading history...</span>
    </div>
        
    <div v-else-if="historyEntries.length === 0" class="empty-state">      
      <p>No disbursement history available.</p>
    </div>
    
    
    <div v-else class="history-list">
      <div
      v-for="entry in historyEntries"
      :key="entry.id"
      class="history-entry"
      :class="{ 'is-latest': isLatestPostDisbChange(entry) }"
      >      
        <div class="entry-header">
          <div class="entry-meta">
            <!-- Use the backend-provided sequencesText -->
            <span class="cert-info">{{ entry.sequencesText }}</span>
            <span class="separator">|</span>
            <span class="timestamp">{{ entry.createdAtFormatted }}</span>
            <span class="separator">|</span>
            <span class="user">{{ entry.userDisplayName }}</span>
          </div>
          
          <PermissionsWrapper
          :disabled="!certification.isEditable"
          :showDisabledIfNotVisible="true"
          :permissionIds="[Permissions.CertAndDisburse]"
          :permissionLevel="PermissionLevels.Update"
          >
          <div 
            v-if="shouldShowMenu(entry)" 
            class="menu-container"
          >
            <button 
            class="kebab-btn"
            @click.stop="toggleMenu(entry.id)"
            :class="{ active: activeMenuId === entry.id }"
            >
            ⋮
            </button>            
            <div 
            v-if="activeMenuId === entry.id" 
            class="dropdown-menu"
            @click.stop
            >
            <button 
              class="menu-item"
              @click="handleEditPostDisbursement(entry)"
            >                    
              Edit
            </button>
            <button v-if="props.certification.canGenerateModificationFile"
              class="menu-item"
              @click="handleGenerateModFile(entry)"
            >                    
              Generate Modification File
            </button>
            </div>
          </div>
          </PermissionsWrapper>
        </div>				
        <div class="entry-body">
          <div class="comment-text">{{ entry.comment }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { CertificationVM } from '@/types/certifications';
import { DisbursementHistory } from '@/types/disbursements';
import { $api } from '@/services/api1';
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import functions from '@/use/functions';

const props = defineProps<{
  certification: CertificationVM;
}>();

const emit = defineEmits<{
  (e: 'editPostDisbursement', data: {
	disbursementId: number;
	postDisbursementChangeAmountLedgerId: number;
  }): void;
}>();

const isLoading = ref(false);
const historyEntries = ref<DisbursementHistory[]>([]);
const activeMenuId = ref<number | null>(null);

const latestPostDisbChanges = computed(() => {
  const latest = new Map<number, number>(); // disbursementId -> postDisbChangeAmountLedgerId
  const postDisbEntries = historyEntries.value
	.filter(e => e.postDisbursementChangeAmountLedgerId != null);
  
  for (const entry of postDisbEntries) {
	const disbId = entry.disbursementId;
	const postDisbId = entry.postDisbursementChangeAmountLedgerId;
	
	const currentLatest = latest.get(disbId);
    if (!currentLatest || postDisbId > currentLatest) {
      latest.set(disbId, postDisbId);
    }
  }
  
  return latest;
});

onMounted(async () => {
  await loadHistory();
  document.addEventListener('click', closeAllMenus);
});

onUnmounted(() => {
  document.removeEventListener('click', closeAllMenus);
});

async function loadHistory() {
  isLoading.value = true;
  try {
    const response = await $api.certifications.getDisbursementHistory({
      certificationId: props.certification.form.id,
      appId: props.certification.appId,
      loanId: props.certification.loanId,
    });
    
    if (response.success && response.results) {
      historyEntries.value = response.results.entries || [];	  
    } else {  
      functions.openModal({
      title: 'Error',
      description: 'Failed to load disbursement history.',
      });
    }
  } catch (error) {	
    functions.openModal({
      title: 'Error',
      description: 'An error occurred while loading history.',
    });
  } finally {
	  isLoading.value = false;
  }
}

function isLatestPostDisbChange(entry: DisbursementHistory): boolean {
  if (!entry.postDisbursementChangeAmountLedgerId) return false;
  
  const latestId = latestPostDisbChanges.value.get(entry.disbursementId);
  return latestId === entry.postDisbursementChangeAmountLedgerId;
}

function shouldShowMenu(entry: DisbursementHistory): boolean {  
  return isLatestPostDisbChange(entry);
}

function toggleMenu(entryId: number) {
  activeMenuId.value = activeMenuId.value === entryId ? null : entryId;
}

function closeAllMenus() {
  activeMenuId.value = null;
}

function handleEditPostDisbursement(entry: DisbursementHistory) {
  if (!entry.postDisbursementChangeAmountLedgerId) return;
    
  emit('editPostDisbursement', {
	disbursementId: entry.disbursementId,
	postDisbursementChangeAmountLedgerId: entry.postDisbursementChangeAmountLedgerId,
  });
  
  activeMenuId.value = null;
}

async function handleGenerateModFile(entry: DisbursementHistory) {
  if (!entry.postDisbursementChangeAmountLedgerId) return;
  
  activeMenuId.value = null;  
  try {
	const response = await $api.certifications.generateModificationFile(entry.certificationId);
	
	if (response.success) {
	  functions.openModal({
		title: 'Success',
		description: 'Modification file has been generated successfully.',
	  });
	} else {
	  functions.openModal({
		title: 'Error',
		description: 'Failed to generate modification file.',
	  });
	}
  } catch (error) {	
    functions.openModal({
      title: 'Error',
      description: 'An error occurred while generating the modification file.',
    });
  }
}
</script>

<style scoped>
.disbursement-history {
  padding: 20px;
  max-width: 1200px;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
  gap: 12px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4CAF50;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;  
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.history-entry {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
}

.history-entry:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.history-entry.is-latest {
  border-left: 4px solid #4CAF50;
}

.entry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.entry-meta {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cert-info {
  font-weight: 600;
  color: #333;
}

.separator {
  color: #ccc;
}

.timestamp,
.user {
  color: #666;
}

.menu-container {
  position: relative;
}

.kebab-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
  color: #999;
  border-radius: 4px;
  transition: all 0.2s;
}

.kebab-btn:hover,
.kebab-btn.active {
  background: #f5f5f5;
  color: #333;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 240px;
  z-index: 1000;
  overflow: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: background 0.2s;
}

.menu-item:hover {
  background: #f8f8f8;
}

.menu-item:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.entry-body {
  line-height: 1.6;
}

.comment-text {
  font-size: 14px;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
}

@media (max-width: 768px) {
  .disbursement-history {
	padding: 12px;
  }
  
  .entry-meta {
	font-size: 12px;
  }
  
  .dropdown-menu {
	right: -8px;
  }
}
</style>